import React, { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { useAppDispatch } from '../../app/hooks';
import Offset from './Offset';
import Upload from '../molecules/Upload';
import EditForm from './EditForm';
import {
  createVenueFile,
  updateVenueFile,
  deleteVenueFile,
} from '../../features/venue/venueSlice';
import {
  createServiceProviderFile,
  updateServiceProviderFile,
  deleteServiceProviderFile,
} from '../../features/serviceProvider/serviceProviderSlice';
import { deleteFile, updateFile, uploadFile } from '../../lib/apis/file';
import { clearObj, getErrorMsg } from '../../lib/utils';
import { display } from '../../data/display';
import Input from '../atoms/Input';
import axios from 'axios';
import InputToggle from '../molecules/InputToggle';

interface ItemMediaFormProps {
  venueID?: string;
  serviceProviderID?: string;
  prefill?: any;
  onSubmit?: (id: string, data: any) => void;
  onCancel?: () => void;
}

const ItemMediaForm = (props: ItemMediaFormProps) => {
  const { venueID, serviceProviderID, prefill, onSubmit, onCancel } = props;
  const { id, name, title, description, size, file, href } =
    (prefill as any) || {};
  const itemType = !!venueID ? 'venue' : 'service';
  const { t } = useTranslation(['common', 'venue', 'service']);
  const dispatch = useAppDispatch();
  const updateItemState = !!venueID
    ? updateVenueFile
    : updateServiceProviderFile;
  const deleteState = !!venueID ? deleteVenueFile : deleteServiceProviderFile;
  const createState = !!venueID ? createVenueFile : createServiceProviderFile;
  const [loading, setLoading] = useState(false);
  const [fileType, setFileType] = useState('upload');
  const [error, setError] = useState() as any;
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const [selectedCategory, setSelectedType] = useState();
  const [files, setFiles] = useState([]) as any;
  const formID = 'ItemMediaForm';

  const planData = display[itemType].platinum as any;
  const category = selectedCategory || prefill.category || 'marketing';
  const displayObj = planData.media || {};
  const currMediaKeys = Object.keys(displayObj);
  const categoryOptions = currMediaKeys.map((key) => ({
    value: key,
    label: t(`${itemType}:media:${key}`),
  }));

  /**
   * Handle errors
   */
  const handleError = (error: any) => {
    const errorMsg = getErrorMsg(error);
    setError(errorMsg);
    setLoading(false);
  };

  /**
   * Handle Item
   */

  //Add MediaItem
  const createItem = (obj: any) => {
    updateFiles(obj);
  };

  //Update MediaItem
  const updateItem = (id: string, obj: any) => {
    try {
      // Save updated MediaItem to database and update venueState
      updateFile(id, obj)
        .then((response) => {
          console.log('updateFile', obj, response);
          setLoading(false);
          dispatch(updateItemState(response));
          if (!!onSubmit) {
            onSubmit(formID, response);
          }
          // updateFiles(response);
        })
        .catch((error) => {
          handleError(error);
          return false;
        });
    } catch (error) {
      handleError(error);
    }
  };

  // Delete MediaItem
  const deleteItem = (id: string, obj: any) => {
    try {
      // Delete MediaItem from database and update venueState
      deleteFile(id)
        .then((response) => {
          // console.log('deletedFile', obj, response);
          setLoading(false);
          dispatch(deleteState(id));
          if (!!onSubmit) {
            onSubmit(formID, response);
          }
        })
        .catch((error) => {
          handleError(error);
          return false;
        });
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * Handle upload
   */
  const updateFiles = async (payload: any) => {
    const newFiles = files;
    if (!!newFiles.length) {
      const uploaded = [] as any;
      // console.log('upload files', newFiles);
      for (const file of newFiles) {
        const obj = clearObj({
          ...payload,
          file,
        });

        uploadFile(obj)
          .then((response: any) => {
            // logoField.value = '';
            uploaded.push(response);
            // console.log('uploaded file', response);
            if (uploaded.length === newFiles.length) {
              // field.value = '';
              completeSubmit(response);
            }
          })
          .catch((error) => {
            handleError(error);
            return false;
          });
      }
    } else {
      const response = await axios.post('/api/file', payload);
      completeSubmit(response);
    }
  };

  const completeSubmit = (result: any) => {
    // console.log('completeSubmit', result);
    // setRemovedFiles([]);
    dispatch(createState(result));
    // dispatch(updateVenueFile(result));
    setLoading(false);
    if (!!onSubmit) {
      onSubmit(formID, result);
    }
  };

  /**
   * Handle form submit
   */
  const submit = (formData: any, deleteMode?: boolean) => {
    const noFileOrHref = !files.length && !formData.href;

    if (!deleteMode && !id && noFileOrHref) {
      setError(t('common:form-input-error'));
      return;
    }

    const payload = {
      ...clearObj({
        ...formData,
        venue_id: venueID,
        service_provider_id: serviceProviderID,
      }),
    };
    setError(false);
    setLoading(true);
    if (!!deleteMode && !!id) {
      deleteItem(id, payload);
    } else if (!!id) {
      updateItem(id, formData);
    } else {
      createItem(payload);
    }
  };

  const cancel = () => {
    if (!!onCancel) {
      onCancel();
    }
  };

  return (
    <EditForm
      checkOnce
      deletable={!!id}
      // deleteOnly={!!id}
      id={formID}
      title={
        title ||
        name ||
        t(`${itemType}:media:headline`) ||
        t(`${itemType}:media:${category}`)
      }
      error={error}
      loading={loading}
      onSubmit={submit}
      onCancel={cancel}
      onFieldError={(arr: any) => setFieldErrors(arr)}
    >
      <Offset>
        <Input
          full
          // disabled={!!id}
          name="category"
          icon="document"
          label={t('common:category')}
          defaultValue={category || categoryOptions[0].value}
          options={categoryOptions}
          // onChange={(obj) => {
          //   const currType = categoryOptions[obj.category] || 'marketing';
          //   // setSelectedTitle(obj.title);
          //   setSelectedType(currType);
          // }}
        />
        <Input
          half
          optional
          // disabled={!!id}
          name="title"
          icon="description"
          error={fieldErrors.indexOf('title') > -1}
          label={t('common:title')}
          defaultValue={title || name || undefined}
        />
        <Input
          half
          optional
          // disabled={!!id}
          name="description"
          icon="description"
          error={fieldErrors.indexOf('description') > -1}
          label={t('common:description')}
          defaultValue={description || undefined}
        />
        <Input
          full
          name="file_type"
          icon="document"
          label="File Type"
          defaultValue="upload"
          options={[
            {
              label: 'Upload',
              value: 'upload',
            },
            {
              label: 'URL',
              value: 'url',
            },
          ]}
          onChange={({ file_type }) => {
            setFileType(file_type);
          }}
        />
        {fileType === 'upload' && !id && (
          <Upload
            half
            name={'file'}
            data={file}
            optional
            onChange={(files) => {
              setFiles(files);
            }}
            onError={(error) => {
              setError(error);
            }}
          />
        )}

        {fileType === 'url' && (
          <Input
            full
            name="href"
            icon="href"
            label={t('common:upload-url')}
            onError={(error) => {
              setError(error);
            }}
            defaultValue={href || undefined}
          />
        )}
      </Offset>
    </EditForm>
  );
};

export default ItemMediaForm;
